<template>
  <modal
    height="620px"
    width="620px"
    :title="t('Totals Settings')"
    @close="close"
    bodyOverflow="auto"
    :read-article-url="`https://docs.bemob.com/${locale}/columns-settings`"
  >
    <template #default>
      <ui-info class="mb-16">
        <span v-html="t(`You can configure the type of values in the total line: sum or average value of the metrics for the selected period.<br> Default Total refers to counting the values basing on the totals line.<br> Sum - the total value is counted as sum of the values line by line.<br> Average - the total value is counted as sum of the values line by line divided on amount of lines.<br> See more details <a target='_blank' href='{0}'>here</a>`,[`https://docs.bemob.com/${locale}/available-metrics`])"></span>
      </ui-info>
      <div class="total-table" v-if="allNumberMetrics.length">
        <div class="total-table__tr--header">
          <div class="total-table__td total-table__td--1">{{t('Column')}}</div>
          <div class="total-table__td total-table__td--2">{{t('Default Total')}}</div>
          <div class="total-table__td total-table__td--2">{{t('____x.sum')}}</div>
          <div class="total-table__td total-table__td--3">{{t('Average')}}</div>
        </div>
        <div
          v-for="item in allNumberMetrics"
          :key="item"
          class="total-table__tr--body"
        >
          <div class="total-table__td total-table__td--1">{{reportMetricsName(item)}}</div>
          <ui-radio-group v-model="metricsSettings[item]" class="radio_group" styleType="inline">
            <div class="total-table__td total-table__td--2"><ui-radio class="circle" value="def"></ui-radio></div>
            <div class="total-table__td total-table__td--2"><ui-radio class="circle" value="sum"></ui-radio></div>
            <div class="total-table__td total-table__td--3"><ui-radio class="circle" value="avg"></ui-radio></div>
          </ui-radio-group>
        </div>
      </div>
      <div v-else>
        {{t('No columns available')}}
      </div>
    </template>
    <template #footer>
      <section class="justify-end align-items-center xs12" style="display: flex; width: 100%">
        <div class="d-flex justify-end">
          <ui-button
            icon="fas fa-times"
            :text="t('Cancel')"
            @click="close"
          ></ui-button>
          <ui-button
            button-style="accent"
            icon="fas fa-check"
            :text="t('Save')"
            @click="submit"
          ></ui-button>
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import {
  useColumnsSettingsDialog,
  useI18n,
  useReportMetrics,
  useReportParams,
} from '@/composables';
import { useStore } from '@/store';
import { reportTypeFromRoute } from '@/services/utils/reportType';
import { resolveReportTypeFromRoute } from '@/views/reports/report-route-behavior';
import { putColumnSettingsApiRequest } from '@/services/api/column-settings-api';
import { showTotalTypeForColumn } from '@/views/formatters';
import { $emitter } from '@/services/emitter';

const emit = defineEmits<{
  close: [];
}>();

const { t, locale } = useI18n();
const route = useRoute();
const store = useStore();
const reportMetricsApi = useReportMetrics();
const reportParamsApi = useReportParams();
const {
  isMetricAllowToInherit,
  allowToSaveThisMetricOnServer,
} = useColumnsSettingsDialog();

const metricsSettings = ref<Record<string, string>>({});

const storeColumns = computed(() => {
  return resolveReportTypeFromRoute(route.name, route.params.type as string) as string;
});

const savedColumns = computed(() => {
  return reportParamsApi.getSavedColumnsForCurrentTab()[storeColumns.value] as {
    columns: string[];
    totalType?: Record<string, string>;
    context: string;
    order: unknown;
    width: Record<string, number>;
  };
});

const allNumberMetrics = computed(() => {
  const columns = savedColumns.value?.columns ?? [];
  return columns.filter((item) => showTotalTypeForColumn(item, store.getCustomColumns));
});

const reportMetricsName = (item: string) => {
  return reportMetricsApi.reportMetricsName(item);
};

const close = () => {
  emit('close');
};

onMounted(() => {
  metricsSettings.value = allNumberMetrics.value.reduce((accumulator, item) => {
    accumulator[item] = savedColumns.value?.totalType?.[item] || 'def';
    return accumulator;
  }, {} as Record<string, string>);
});

const submit = () => {
  const currentType = reportTypeFromRoute() as string;
  const inheritColumns = !store.user.independentColumnSettingsEnabled;
  const { groupBy } = reportParamsApi.getReportParams();
  const groupByValue = (Array.isArray(groupBy) ? groupBy[0] : groupBy) as string;
  const contexts = inheritColumns ? Object.keys(reportParamsApi.savedColumnsInCurentPreset.value) : [groupByValue];

  const resultColumns = contexts.reduce((accumulator, dimension: string) => {
    const savedContextData = reportParamsApi.savedColumnsInCurentPreset.value[dimension];

    const columnsContext = {
      context: savedContextData.context,
      order: savedContextData.order,
      columns: savedContextData.columns.map((column: string) => ({
        name: column,
        width: savedContextData.width[column],
        ...(['avg', 'sum'].includes(metricsSettings.value[column]) && { totalType: metricsSettings.value[column] }),
      })),
    };

    if (!!store.table.activeTab && currentType !== 'live-clicks' && currentType !== 'live-visits') {
      accumulator.local.push(columnsContext);
    } else if (isMetricAllowToInherit(inheritColumns, dimension, currentType)) {
      const { columnsFront, widthFront, totalType } = columnsContext.columns.reduce((columnAccumulator, { name, width, totalType: columnTotalType }) => {
        columnAccumulator.widthFront[name] = width as number;
        columnAccumulator.columnsFront.push(name);
        if (columnTotalType) {
          columnAccumulator.totalType[name] = columnTotalType;
        }
        return columnAccumulator;
      }, { columnsFront: [] as string[], widthFront: {} as Record<string, number>, totalType: {} as Record<string, string> });
      accumulator.front[dimension] = {
        ...columnsContext,
        columns: columnsFront,
        width: widthFront,
        totalType,
      };

      if (allowToSaveThisMetricOnServer.value) {
        accumulator.server.push(columnsContext);
      }
    }

    return accumulator;
  }, { front: { ...store.getDefaultColumns() }, server: [] as unknown[], local: [] as unknown[] });

  if (currentType === 'live-visits' || currentType === 'live-clicks' || !store.table.activeTab) {
    store.setLoading(true);
    putColumnSettingsApiRequest({
      id: reportParamsApi.activePreset.value,
      name: reportParamsApi.activePresetData.value?.presetName,
      settings: resultColumns.server,
    }).finally(() => {
      store.updatePreset({
        presetId: reportParamsApi.activePreset.value,
        presetData: resultColumns.front,
      });
      $emitter.emit('reload-report');
      store.setLoading(false);
      close();
    });
  }

  if (resultColumns.local.length) {
    store.applyActiveReportColumnsFromContexts(resultColumns.local as Parameters<typeof store.applyActiveReportColumnsFromContexts>[0]);
    close();
  }
};
</script>

<style lang="scss" scoped>
.total-table__tr--header {
  font-weight: bold;
}

.total-table__tr--body,
.total-table__tr--header {
  display: flex;
  padding: 8px 0;
}

.total-table__tr--body {
  border-top: 1px solid var(--color-gray10);

  :deep(.ui-radio) {
    align-items: center;
  }

  :deep(.ui-icon) {
    margin-right: 0;
  }
}

.total-table__td {
  display: flex;
  align-items: center;
}

.total-table__td--2,
.total-table__td--3,
.total-table__td--4 {
  display: flex;
  justify-content: center;
}

.total-table__td--1 {
  flex-grow: 1;
}

.total-table__td--2 {
  width: 80px;

  &:lang(es) {
    min-width: 100px;
  }

  &:lang(ru) {
    min-width: 110px;
  }
}

.total-table__td--3 {
  width: 57px;

  &:lang(ru) {
    min-width: 77px;
  }
}

.total-table__td--4 {
  width: 79px;

  &:lang(ru) {
    min-width: 99px;
  }
}
</style>
